import { isString } from "lodash";

import { OptionType } from "components/customInputs/MultiColumnSelectInput";

export type FilterObject = { [key: string]: string | string[] };

// Type guard to safely detect OptionType[]
const isOptionTypeArray = (val: unknown): val is OptionType[] =>
  Array.isArray(val) &&
  val.length > 0 &&
  typeof val[0] === "object" &&
  val[0] !== null &&
  "value" in val[0] &&
  "label" in val[0];

const mapOptionsToValues = (options: OptionType[]) => {
  return options.map((option) => String(option.value));
};

const mapOptionsToValuesString = (options: OptionType[]) => {
  return options.map((option) => option.value).join(",");
};

export const reduceFiltersToApi = (filters: FilterObject) => {
  return Object.keys(filters).reduce((acc: FilterObject, filter: string) => {
    const value = filters[filter];

    if (value && value.length) {
      if (isOptionTypeArray(value)) {
        acc[filter] = mapOptionsToValues(value);

        return acc;
      }

      const isDate =
        isString(value) && (value as string).match(/^\d{4}-\d{2}-\d{2}$/);

      acc[filter] =
        filter.includes("After") && isDate
          ? new Date(`${value}T00:00:00`).toISOString()
          : filter.includes("Before") && isDate
          ? new Date(`${value}T23:59:59`).toISOString()
          : value;
    }
    return acc;
  }, {});
};

export const reduceFiltersForSearchParams = (filters: FilterObject) => {
  const f = Object.keys(filters).reduce((acc: FilterObject, filter: string) => {
    const value = filters[filter];

    if (
      value &&
      (Array.isArray(value) ? value.length : (value as string).length)
    ) {
      if (isOptionTypeArray(value)) {
        acc[filter] = mapOptionsToValuesString(value);
      } else {
        acc[filter] = value;
      }
    }
    return acc;
  }, {});

  // swap keys to maintain backwards compatibility with bookmarks
  if (f["patientClinicSonicPracticeName"]) {
    if (!f["sonicPracticeName"])
      f["sonicPracticeName"] = f["patientClinicSonicPracticeName"];
    delete f["patientClinicSonicPracticeName"];
  }

  return f;
};

export const resetFilters = (filters: FilterObject) => {
  return Object.keys(filters).reduce(
    (results: FilterObject, filterKey: string) => {
      results[filterKey] = "";
      return results;
    },
    {}
  );
};

export const resetBooleanFilters = (filters: {
  [key: string]: boolean | undefined;
}) => {
  return Object.keys(filters).reduce(
    (results: { [key: string]: boolean | undefined }, key: string) => {
      results[key] =
        key === "isManuallyAccessioned" ||
        key === "dataEntry" ||
        key === "billingEntry"
          ? undefined
          : false;
      return results;
    },
    {}
  );
};
